<template>
  <div class="user-management">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">
            <v-icon size="28" class="mr-3">mdi-account-group</v-icon>
            Gestión de Usuarios
          </h1>
          <p class="page-subtitle">Gestiona usuarios del sistema, roles y permisos</p>
        </div>

        <div class="header-actions">
          <v-btn
            @click="openCreateDialog"
            color="primary"
            size="large"
            class="create-user-btn"
            prepend-icon="mdi-account-plus"
          >
            Crear usuario
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-section mb-6">
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="32" color="primary" class="mb-2"> mdi-account-multiple </v-icon>
              <div class="stats-number">{{ userStore.totalUsers }}</div>
              <div class="stats-label">Total de usuarios</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="32" color="success" class="mb-2"> mdi-account-check </v-icon>
              <div class="stats-number">{{ userStore.activeUsers }}</div>
              <div class="stats-label">Usuarios activos</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="32" color="error" class="mb-2"> mdi-account-cancel </v-icon>
              <div class="stats-number">{{ userStore.inactiveUsers }}</div>
              <div class="stats-label">Usuarios inactivos</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="text-center">
              <v-icon size="32" color="warning" class="mb-2"> mdi-shield-crown </v-icon>
              <div class="stats-number">{{ adminCount }}</div>
              <div class="stats-label">Administradores</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- User List -->
    <UserList
      :users="userStore.users"
      :loading="userStore.loading"
      :pagination="userStore.pagination"
      @edit-user="openEditDialog"
      @deactivate-user="openDeactivateDialog"
      @activate-user="handleActivateUser"
      @filter-change="handleFilterChange"
      @page-change="handlePageChange"
      @items-per-page-change="handleItemsPerPageChange"
    />

    <!-- User Form Dialog -->
    <UserForm
      v-model:dialog="showUserForm"
      :user="selectedUser"
      :loading="formLoading"
      @submit="handleUserSubmit"
      @cancel="closeUserForm"
      ref="userFormRef"
    />

    <!-- Confirm Deactivate Dialog -->
    <ConfirmDialog
      v-model:dialog="showDeactivateDialog"
      title="Desactivar Usuario"
      :message="`¿ Estás seguro de que quieres desactivar este usuario? Ya no podrá acceder al sistema.`"
      confirm-text="Desactivar"
      confirm-color="error"
      :loading="actionLoading"
      :user-info="selectedUser"
      @confirm="handleDeactivateUser"
      @cancel="closeDeactivateDialog"
    />

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="4000"
      location="top right"
      class="custom-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">
          {{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
        </v-icon>
        {{ snackbarMessage }}
      </div>
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useUserStore } from '@/stores/user'
import UserList from '@/components/admin/UserList.vue'
import UserForm from '@/components/admin/UserForm.vue'
import ConfirmDialog from '@/components/admin/ConfirmDialog.vue'

const userStore = useUserStore()

const showUserForm = ref(false)
const showDeactivateDialog = ref(false)
const selectedUser = ref(null)
const formLoading = ref(false)
const actionLoading = ref(false)
const userFormRef = ref(null)

const showSnackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

const adminCount = computed(() => {
  return userStore.users.filter((user) => user.role === 'admin').length
})

onMounted(async () => {
  await loadUsers()
})

const loadUsers = async () => {
  await loadUsersWithPagination(1, 10)
}

const openCreateDialog = () => {
  selectedUser.value = null
  showUserForm.value = true
}

const openEditDialog = (user) => {
  selectedUser.value = user
  showUserForm.value = true
}

const closeUserForm = () => {
  showUserForm.value = false
  selectedUser.value = null
  if (userFormRef.value) {
    userFormRef.value.resetForm()
  }
}

const handleUserSubmit = async (userData) => {
  formLoading.value = true

  try {
    let result
    const isEditing = !!selectedUser.value?.id

    if (isEditing) {
      result = await userStore.updateUser(selectedUser.value.id, userData)
    } else {
      result = await userStore.createUser(userData)
    }

    if (result.success) {
      closeUserForm()
      await loadUsersWithPagination(userStore.pagination.page, userStore.pagination.perPage)

      const action = isEditing ? 'actualizado' : 'creado'
      showSuccessMessage(`Usuario ${action} exitosamente`)
    } else {
      if (result.error && typeof result.error === 'object') {
        if (userFormRef.value) {
          userFormRef.value.setFieldErrors(result.error)
        }
      } else {
        if (userFormRef.value) {
          userFormRef.value.setGeneralError(result.error || 'Ha ocurrido un error')
        }
      }
    }
  } catch (error) {
    if (userFormRef.value) {
      userFormRef.value.setGeneralError('Ha ocurrido un error inesperado')
    }
  } finally {
    formLoading.value = false
  }
}

const openDeactivateDialog = (user) => {
  selectedUser.value = user
  showDeactivateDialog.value = true
}

const closeDeactivateDialog = () => {
  showDeactivateDialog.value = false
  selectedUser.value = null
}

const handleDeactivateUser = async () => {
  if (!selectedUser.value) return

  actionLoading.value = true

  try {
    const result = await userStore.deactivateUser(selectedUser.value.id)

    if (result.success) {
      closeDeactivateDialog()
      await loadUsersWithPagination(userStore.pagination.page, userStore.pagination.perPage)
      showSuccessMessage('Usuario desactivado exitosamente')
    } else {
      showErrorMessage(result.error || 'Error al desactivar usuario')
    }
  } catch (error) {
    showErrorMessage('Ha ocurrido un error inesperado')
  } finally {
    actionLoading.value = false
  }
}

const handleActivateUser = async (user) => {
  actionLoading.value = true

  try {
    const result = await userStore.activateUser(user.id)

    if (result.success) {
      await loadUsersWithPagination(userStore.pagination.page, userStore.pagination.perPage)
      showSuccessMessage('Usuario activado exitosamente')
    } else {
      showErrorMessage(result.error || 'Error al activar usuario')
    }
  } catch (error) {
    showErrorMessage('Ha ocurrido un error inesperado')
  } finally {
    actionLoading.value = false
  }
}

const handleFilterChange = async (filters) => {
  userStore.setFilters(filters)
  // Recargar usuarios con nuevos filtros
  await loadUsers()
}

const handlePageChange = async (page) => {
  await loadUsersWithPagination(page, userStore.pagination.perPage)
}

const handleItemsPerPageChange = async (itemsPerPage) => {
  await loadUsersWithPagination(1, itemsPerPage)
}

const loadUsersWithPagination = async (page = 1, perPage = 10) => {
  const result = await userStore.fetchUsers(page, perPage)
  if (!result.success) {
    showErrorMessage('Error al cargar usuarios')
  }
}

const showSuccessMessage = (message) => {
  snackbarMessage.value = message
  snackbarColor.value = 'success'
  showSnackbar.value = true
}

const showErrorMessage = (message) => {
  snackbarMessage.value = message
  snackbarColor.value = 'error'
  showSnackbar.value = true
}
</script>

<style scoped>
.user-management {
  padding: 24px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.page-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  color: #0a192f;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.page-subtitle {
  color: #64748b;
  font-size: 1.1rem;
  margin: 0;
}

.create-user-btn {
  background: linear-gradient(135deg, #0a192f 0%, #112240 100%) !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
  border-radius: 12px;
  padding: 0 24px;
  height: 48px;
  box-shadow: 0 4px 12px rgba(10, 25, 47, 0.3);
  transition: all 0.2s ease;
}

.create-user-btn:hover {
  box-shadow: 0 6px 16px rgba(10, 25, 47, 0.4);
  transform: translateY(-2px);
}

.stats-section {
  margin-bottom: 24px;
}

.stats-card {
  border-radius: 16px !important;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.stats-card:hover {
  box-shadow: 0 8px 24px rgba(10, 25, 47, 0.15) !important;
  transform: translateY(-2px);
}

.stats-number {
  font-size: 2rem;
  font-weight: 700;
  color: #0a192f;
  margin-bottom: 4px;
}

.stats-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.custom-snackbar {
  border-radius: 12px;
}

.custom-snackbar :deep(.v-snackbar__wrapper) {
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .user-management {
    padding: 16px;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .page-title {
    font-size: 1.75rem;
  }

  .create-user-btn {
    width: 100%;
    justify-content: center;
  }

  .stats-section {
    margin-bottom: 16px;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 1.5rem;
  }

  .page-subtitle {
    font-size: 1rem;
  }
}
</style>
